import { useMemo, useState } from 'react';
import { ShowPageLayout } from 'framework/ui/layout';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { Button, Loading } from 'framework/ui/mui-builder';
import {
  APPROVE_CUSTOMER_REPORT,
  APPROVE_REJECT_REPORT_RECIPIENT,
  CANCEL_CUSTOMER_REPORT,
  REJECT_CUSTOMER_REPORT,
  SEND_CUSTOMER_REPORT,
  useNewProjectUrl,
  CUSTOMER_REPORT_STATUS,
} from 'domain/shared/newProject';
import SendReportModal from '../sendReport/SendReportModal';
import { DATE_FORMAT, useDateService } from 'framework/utils';
import { useAuthorization } from 'framework/authorization';
import { getRenderingApiUrl } from 'infrastructure/pdf';
import useCustomerReportService from '../useCustomerReportService';
import { FormFieldsData, ModalForm, TextArea } from 'framework/form';
import { Modal } from 'framework/ui/ui-builder';
import { ApprovalLayout } from 'framework/ui/ui-builder/approval';
import { useMessageService, ErrorMessage, SuccessMessage } from 'framework/ui/Message';
import { useCustomerReportTemplateData } from '../hooks/useCustomerReportTemplateData';
import { generatePdf, markdownToHtml, replacePlaceholders, TemplatePreview } from 'framework/markdownEditor';

export const blobToBase64 = async (blob: Blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // @ts-ignore
      const base64 = reader.result?.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const ApproveCustomerReportLayout = () => {
  const { id, reportId } = useParams();
  const navigate = useNavigate();
  const { isGranted } = useAuthorization();

  const { placeholders, reportData, html, refetch } = useCustomerReportTemplateData(id!, reportId!);
  const { getCustomerReportUrl } = useNewProjectUrl();
  const {
    sendSyberryReport,
    cancelCustomerReport,
    approveCustomerReport,
    rejectCustomerReport,
    getCustomerReportRejectValidationSchema,
  } = useCustomerReportService();
  const { formatDate } = useDateService();
  const { addMessage } = useMessageService();

  const [rejectionModalOpen, setRejectionModalOpen] = useState(false);
  const [inProgress, setInProgress] = useState(false);

  const cardTitle = useMemo(() => {
    if (
      reportData?.status === CUSTOMER_REPORT_STATUS.IN_APPROVAL &&
      isGranted([APPROVE_CUSTOMER_REPORT, REJECT_CUSTOMER_REPORT])
    ) {
      return 'Approve Customer Report';
    } else if (reportData?.status === CUSTOMER_REPORT_STATUS.APPROVED) {
      return 'Send Customer Report';
    } else {
      return 'Customer Report';
    }
  }, [isGranted, reportData?.status]);

  const handleApprove = async () => {
    setInProgress(true);
    return approveCustomerReport(reportId!, {})
      .then(() => {
        refetch();
        addMessage(new SuccessMessage('You are awesome! Customer Report was approved'));
      })
      .catch(e => {
        addMessage(new ErrorMessage('Error occurred during approving Customer Report: ' + e.message));
      })
      .finally(() => setInProgress(false));
  };

  if (!html) {
    return <Loading />;
  }

  const canDecide =
    reportData?.status === CUSTOMER_REPORT_STATUS.IN_APPROVAL &&
    isGranted([APPROVE_CUSTOMER_REPORT, REJECT_CUSTOMER_REPORT, APPROVE_REJECT_REPORT_RECIPIENT]);

  const canSend = reportData?.status === CUSTOMER_REPORT_STATUS.APPROVED && isGranted(SEND_CUSTOMER_REPORT);

  const period = `${formatDate(reportData?.periodStart, DATE_FORMAT.NATURAL)} — ${formatDate(reportData?.periodEnd, DATE_FORMAT.NATURAL)}`;

  const titleNode = (
    <>
      {cardTitle}
      <Typography variant="body1" color="textSecondary" mt={0.5}>
        {period}
      </Typography>
    </>
  );

  const sendActions = canSend ? (
    <SendReportModal
      projectId={id}
      disabled={!reportData?.periodStart || !reportData?.periodEnd}
      titleInterval={period}
      contentGenerator={() =>
        generatePdf(
          getRenderingApiUrl(),
          markdownToHtml(replacePlaceholders(reportData.template, placeholders ?? {})),
          {},
        ).then(blobToBase64)
      }
      onSubmit={(submitBody: FormFieldsData) => sendSyberryReport(reportId!, submitBody)}
    />
  ) : undefined;

  return (
    <ShowPageLayout>
      <ApprovalLayout
        title={titleNode}
        titleVariant="h2"
        onApprove={canDecide ? handleApprove : undefined}
        onReject={canDecide ? () => setRejectionModalOpen(true) : undefined}
        isSubmitting={inProgress}
        actions={sendActions}
      >
        <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between' }}>
          <TemplatePreview
            showTitle={false}
            maxHeight={'65vh'}
            paperSx={{
              pr: 2,
              width: '70%',
              '& h2, & h1, & h3, & h4': { margin: '24px 0' },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                margin: '24px 0',
              },
              '& th, & td': {
                border: '1px solid var(--mui-palette-border-default)',
                padding: '8px',
                textAlign: 'left',
                wordWrap: 'break-word',
              },
              '& th': {
                backgroundColor: 'var(--mui-palette-background-default)',
                fontWeight: 'bold',
              },
            }}
            html={html}
            title=""
          />

          {reportData?.status !== CUSTOMER_REPORT_STATUS.SENT &&
            reportData?.status !== CUSTOMER_REPORT_STATUS.CANCELED &&
            isGranted(CANCEL_CUSTOMER_REPORT) && (
              <Button
                variant="contained"
                sx={{ backgroundColor: 'error.main' }}
                onClick={() => {
                  cancelCustomerReport(reportId!, {});
                  navigate(getCustomerReportUrl(id!));
                }}
              >
                Cancel report
              </Button>
            )}
        </Box>

        <Modal isOpen={rejectionModalOpen} onToggle={() => setRejectionModalOpen(false)} title="Reason for rejection">
          <ModalForm
            closeModal={() => setRejectionModalOpen(false)}
            modalOpen={rejectionModalOpen}
            getValidationSchemaFunc={getCustomerReportRejectValidationSchema}
            submitFormFunc={values => rejectCustomerReport(reportId!, values.rejectionReason)}
            afterSubmit={{
              redirect: getCustomerReportUrl(id),
              message: 'You are awesome! Rejection was done.',
            }}
          >
            <TextArea source="rejectionReason" label="" placeholder="Please, provide reason here" />
          </ModalForm>
        </Modal>
      </ApprovalLayout>
    </ShowPageLayout>
  );
};
export default ApproveCustomerReportLayout;
